import type { ExpenseCostSplit, ExpenseOwnerShare } from '../api';
import {
  formatOwnershipShare,
  isOwnershipShareSumComplete,
  parseOwnershipShareInput,
} from './ownershipShare';

export const sameIdList = (
  left?: number[] | null,
  right?: number[] | null
) => {
  const a = [...(left || [])]
    .map(Number)
    .filter((id) => id > 0)
    .sort((x, y) => x - y);
  const b = [...(right || [])]
    .map(Number)
    .filter((id) => id > 0)
    .sort((x, y) => x - y);
  return a.length === b.length && a.every((id, i) => id === b[i]);
};

/** Не слать сохранённые id, если список не меняли: иначе правка падает после удаления полёта. */
export const hoursFlightIdsPayload = (
  split: string,
  current: number[],
  saved?: number[] | null
) => {
  if (split !== 'hours') return undefined;
  if (saved != null && sameIdList(current, saved)) return undefined;
  return current.filter((id) => id > 0);
};

export const formatExpenseRub = (value: number) =>
  `${value.toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} ₽`;

/** Целые проценты, сумма всегда 100 (остаток — последнему). */
export const allocateIntegerPercents = (weights: number[]) => {
  const sum = weights.reduce((acc, item) => acc + item, 0);
  if (sum <= 0) return weights.map(() => 0);
  let allocated = 0;
  return weights.map((weight, index) => {
    if (index === weights.length - 1) {
      return Math.max(0, 100 - allocated);
    }
    const part = Math.round((weight / sum) * 100);
    allocated += part;
    return part;
  });
};

const allocateByWeights = (weights: number[], total: number) => {
  const sum = weights.reduce((acc, item) => acc + item, 0);
  if (!Number.isFinite(total) || total < 0 || sum <= 0) {
    return weights.map(() => null as number | null);
  }
  let allocated = 0;
  return weights.map((weight, index) => {
    if (index === weights.length - 1) {
      return Math.round((total - allocated) * 100) / 100;
    }
    const part = Math.round((total * weight) / sum * 100) / 100;
    allocated += part;
    return part;
  });
};

export const sharePreviewRows = (
  owners: ExpenseOwnerShare[],
  amount: number | null
) => {
  const parts = allocateByWeights(
    owners.map((owner) => owner.percent || 0),
    amount ?? -1
  );
  return owners.map((owner, index) => ({
    key: String(owner.user_id),
    name: owner.name,
    detail: formatOwnershipShare(owner.share),
    cost: amount == null || amount < 0 ? null : parts[index],
  }));
};

export type ManualSplitUnit = 'share' | 'rub';

export const parseRubInput = (value: string): number | null | 'invalid' => {
  const text = value.trim().replace(/\s/g, '').replace(',', '.');
  if (text === '') return null;
  const n = Number(text);
  if (!Number.isFinite(n) || n < 0) return 'invalid';
  return Math.round(n * 100) / 100;
};

export const formatRubDraft = (value: number) => {
  if (!Number.isFinite(value)) return '';
  return String(Math.round(value * 100) / 100);
};

export const parseManualShareInput = (value: string) => {
  const parsed = parseOwnershipShareInput(value);
  if (parsed === 'invalid' || parsed == null || parsed <= 0) return 0;
  return parsed;
};

export const manualSharePairs = (shares: Record<number, string>) =>
  Object.entries(shares)
    .map(([id, raw]) => ({
      user_id: Number(id),
      share: parseManualShareInput(raw),
    }))
    .filter((row) => row.user_id > 0 && row.share > 0);

export const manualShareSum = (shares: Record<number, string>) =>
  manualSharePairs(shares).reduce((acc, row) => acc + row.share, 0);

export const isManualShareSumComplete = (shares: Record<number, string>) =>
  isOwnershipShareSumComplete(manualShareSum(shares));

export const manualShareError = (shares: Record<number, string>) => {
  const ids = Object.keys(shares);
  if (ids.length === 0) return 'Отметьте участников и укажите их доли';
  for (const raw of Object.values(shares)) {
    const parsed = parseOwnershipShareInput(raw);
    if (parsed === 'invalid' || parsed == null || parsed <= 0) {
      return 'Укажите долю каждого отмеченного';
    }
  }
  if (!isManualShareSumComplete(shares)) {
    return 'Сумма долей должна быть равна 1';
  }
  return null;
};

export const sharesFromSaved = (
  ids: number[],
  shares?: number[] | null
): Record<number, string> => {
  const values = ids
    .map((id, index) => ({
      id,
      share: shares && shares[index] != null ? Number(shares[index]) : 0,
    }))
    .filter((row) => row.id > 0 && Number.isFinite(row.share) && row.share > 0);
  const sum = values.reduce((acc, row) => acc + row.share, 0);
  const next: Record<number, string> = {};
  values.forEach((row) => {
    next[row.id] = formatOwnershipShare(sum > 0 ? row.share / sum : 0);
  });
  return next;
};

export const sharesFromAllocations = (
  items?: { user_id?: number; share?: number }[] | null
): Record<number, string> => {
  const values = (items || []).filter(
    (item) => item.user_id && (item.share || 0) > 0
  );
  return sharesFromSaved(
    values.map((item) => Number(item.user_id)),
    values.map((item) => Number(item.share))
  );
};

/** Отмеченных делит поровну: 1 → 1, 2 → 1/2, 3 → 1/3. */
export const equalManualShares = (userIds: number[]): Record<number, string> => {
  const ids = Array.from(
    new Set(userIds.filter((id) => Number.isFinite(id) && id > 0))
  );
  const next: Record<number, string> = {};
  if (ids.length === 0) return next;
  const each = 1 / ids.length;
  ids.forEach((id) => {
    next[id] = formatOwnershipShare(each);
  });
  return next;
};

export const toggleManualShare = (
  shares: Record<number, string>,
  userId: number,
  checked: boolean
) => {
  const ids = Object.keys(shares)
    .map(Number)
    .filter((id) => Number.isFinite(id) && id > 0 && id !== userId);
  if (checked) ids.push(userId);
  return equalManualShares(ids);
};

export const equalManualRubles = (
  userIds: number[],
  total: number
): Record<number, string> => {
  const ids = Array.from(
    new Set(userIds.filter((id) => Number.isFinite(id) && id > 0))
  );
  const next: Record<number, string> = {};
  if (ids.length === 0) return next;
  if (!(total > 0)) {
    ids.forEach((id) => {
      next[id] = '';
    });
    return next;
  }
  const parts = allocateByWeights(
    ids.map(() => 1),
    total
  );
  ids.forEach((id, index) => {
    next[id] = formatRubDraft(parts[index] ?? 0);
  });
  return next;
};

export const toggleManualRub = (
  rubles: Record<number, string>,
  userId: number,
  checked: boolean,
  total: number
) => {
  const ids = Object.keys(rubles)
    .map(Number)
    .filter((id) => Number.isFinite(id) && id > 0 && id !== userId);
  if (checked) ids.push(userId);
  return equalManualRubles(ids, total);
};

export const sharesFromRubles = (
  rubles: Record<number, string>,
  total: number
): Record<number, string> => {
  const next: Record<number, string> = {};
  Object.entries(rubles).forEach(([id, raw]) => {
    const userId = Number(id);
    if (!(userId > 0)) return;
    if (!(total > 0)) {
      next[userId] = '';
      return;
    }
    const parsed = parseRubInput(raw);
    if (parsed === 'invalid' || parsed == null || parsed <= 0) {
      next[userId] = '';
      return;
    }
    next[userId] = formatOwnershipShare(parsed / total);
  });
  return next;
};

export const rublesFromShares = (
  shares: Record<number, string>,
  total: number
): Record<number, string> => {
  const ids = Object.keys(shares)
    .map(Number)
    .filter((id) => Number.isFinite(id) && id > 0);
  if (!(total > 0)) {
    const next: Record<number, string> = {};
    ids.forEach((id) => {
      next[id] = '';
    });
    return next;
  }
  const parts = allocateByWeights(
    ids.map((id) => parseManualShareInput(shares[id] || '')),
    total
  );
  const next: Record<number, string> = {};
  ids.forEach((id, index) => {
    next[id] = formatRubDraft(parts[index] ?? 0);
  });
  return next;
};

export const manualRubSum = (rubles: Record<number, string>) => {
  let sum = 0;
  for (const raw of Object.values(rubles)) {
    const parsed = parseRubInput(raw);
    if (parsed === 'invalid' || parsed == null) return Number.NaN;
    sum += parsed;
  }
  return Math.round(sum * 100) / 100;
};

export const isManualRubSumComplete = (
  rubles: Record<number, string>,
  total: number
) => {
  if (!(total > 0) || Object.keys(rubles).length === 0) return false;
  const sum = manualRubSum(rubles);
  return Number.isFinite(sum) && Math.abs(sum - total) <= 0.01;
};

export const weightedPreviewRows = (
  owners: ExpenseOwnerShare[],
  shares: Record<number, string>,
  amount: number | null
) => {
  const selected = owners.filter(
    (owner) => parseManualShareInput(shares[owner.user_id] || '') > 0
  );
  const parts = allocateByWeights(
    selected.map((owner) => parseManualShareInput(shares[owner.user_id] || '')),
    amount ?? -1
  );
  return selected.map((owner, index) => ({
    key: String(owner.user_id),
    name: owner.name,
    detail: formatOwnershipShare(
      parseManualShareInput(shares[owner.user_id] || '')
    ),
    cost: amount == null || amount < 0 ? null : parts[index],
  }));
};

export type HourFlight = {
  id: number;
  pilot_id: number;
  pilot: string;
  hours: number;
};

export const hoursPreviewRows = (
  flights: HourFlight[],
  selectedIds: number[],
  amount: number | null
) => {
  const selected = flights.filter(
    (flight) => selectedIds.includes(flight.id) && (flight.hours || 0) > 0
  );
  const byPilot = new Map<number, { name: string; hours: number }>();
  selected.forEach((flight) => {
    const current = byPilot.get(flight.pilot_id) || {
      name: flight.pilot,
      hours: 0,
    };
    current.hours = Math.round((current.hours + (flight.hours || 0)) * 100) / 100;
    byPilot.set(flight.pilot_id, current);
  });
  const rows = Array.from(byPilot.entries());
  const parts = allocateByWeights(
    rows.map(([, row]) => row.hours),
    amount ?? -1
  );
  const percents = allocateIntegerPercents(rows.map(([, row]) => row.hours));
  return rows.map(([userId, row], index) => ({
    key: String(userId),
    name: row.name,
    detail: `${row.hours} м/ч (${percents[index]}%)`,
    cost: amount == null || amount < 0 ? null : parts[index],
  }));
};

export const toggleFlightId = (ids: number[], flightId: number, checked: boolean) => {
  if (checked) {
    return ids.includes(flightId) ? ids : [...ids, flightId];
  }
  return ids.filter((id) => id !== flightId);
};

export const PENDING_HOUR_FLIGHT_ID = 0;

export const refuelPurchaseTotal = (
  refuels: { liters?: number | null; price_per_liter?: number | null }[]
) =>
  refuels.reduce((sum, item) => {
    const liters = Number(item.liters);
    const price = Number(item.price_per_liter);
    if (!Number.isFinite(liters) || liters <= 0 || !Number.isFinite(price) || price < 0) {
      return sum;
    }
    return Math.round((sum + liters * price) * 100) / 100;
  }, 0);

export type FuelSplitState = {
  costSplit: ExpenseCostSplit;
  manualShares: Record<number, string>;
  flightIds: number[];
  payerUserId: number;
};

export const emptyFuelSplit = (payerUserId = 0): FuelSplitState => ({
  costSplit: 'personal',
  manualShares: {},
  flightIds: [],
  payerUserId,
});

export const fuelSplitFromSaved = (
  item?: {
    cost_split?: ExpenseCostSplit | null;
    payer_ids?: number[] | null;
    payer_shares?: number[] | null;
    flight_ids?: number[] | null;
  } | null,
  fallbackPayer = 0
): FuelSplitState => {
  const payer = item?.payer_ids?.[0] || fallbackPayer;
  return {
    costSplit: item?.cost_split || 'personal',
    manualShares: sharesFromSaved(item?.payer_ids || [], item?.payer_shares),
    flightIds: item?.flight_ids || [],
    payerUserId: payer,
  };
};

export const fuelSplitError = (state: FuelSplitState, hasPurchase: boolean) => {
  if (!hasPurchase) return null;
  if (state.costSplit === 'manual') return manualShareError(state.manualShares);
  if (state.costSplit === 'personal' && !state.payerUserId) {
    return 'Укажите, кто оплатит';
  }
  return null;
};

export const fuelSplitRequest = (state: FuelSplitState, hasPurchase: boolean) => {
  if (!hasPurchase) {
    return {
      cost_split: undefined as ExpenseCostSplit | undefined,
      payer_ids: undefined as number[] | undefined,
      payer_shares: undefined as number[] | undefined,
      flight_ids: undefined as number[] | undefined,
    };
  }
  const pairs = manualSharePairs(state.manualShares);
  return {
    cost_split: state.costSplit,
    payer_ids:
      state.costSplit === 'manual'
        ? pairs.map((row) => row.user_id)
        : state.costSplit === 'personal'
          ? [state.payerUserId]
          : undefined,
    payer_shares:
      state.costSplit === 'manual' ? pairs.map((row) => row.share) : undefined,
    flight_ids:
      state.costSplit === 'hours'
        ? state.flightIds.filter((id) => id > 0)
        : undefined,
  };
};

export const equalPreviewRows = (
  owners: ExpenseOwnerShare[],
  selectedIds: number[],
  amount: number | null
) => {
  const selected = owners.filter((owner) => selectedIds.includes(owner.user_id));
  const parts = allocateByWeights(
    selected.map(() => 1),
    amount ?? -1
  );
  return selected.map((owner, index) => ({
    key: String(owner.user_id),
    name: owner.name,
    detail: 'поровну',
    cost: amount == null || amount < 0 ? null : parts[index],
  }));
};
